import { HttpApiError, HttpApiGroup } from "@effect/platform";
import { Schema } from "effect";
import { endpoint } from "../auth/endpoint";
import { DatabaseError, DataIntegrityError } from "../auth/errors";

import { AccountId, ShopperAiIntegrationId } from "../ids/definitions";
import {
  IntegrationCursor,
  IntegrationWithChannelDataResponse,
  UpdateContactFormIntegrationInput,
} from "../models/shopper-ai/integration/integration";
import {
  CursorPaginatedResponse,
  CursorPaginationParams,
} from "../schema/pagination";

/**
 * Wire-level setting entry. Permissive on purpose: a `key`/`value`
 * pair where the relationship between the two is enforced server-side
 * via the registry's `settingDecoders`. The merchant client wraps this
 * in a typed helper so callers get a strict `K → SettingValueType<K>`
 * binding at compile time.
 */
export const SetIntegrationSettingEntry = Schema.Struct({
  key: Schema.String,
  value: Schema.Unknown,
});
export type SetIntegrationSettingEntry = typeof SetIntegrationSettingEntry.Type;

export const SetIntegrationSettingsPayload = Schema.Struct({
  settings: Schema.Array(SetIntegrationSettingEntry),
});
export type SetIntegrationSettingsPayload =
  typeof SetIntegrationSettingsPayload.Type;

export const SetIntegrationSettingsResponse = Schema.Struct({
  updated: Schema.Number,
  skipped: Schema.Array(
    Schema.Struct({ key: Schema.String, reason: Schema.String }),
  ),
});
export type SetIntegrationSettingsResponse =
  typeof SetIntegrationSettingsResponse.Type;

export const GetTeamIntegrationsParams = Schema.Struct({
  ...CursorPaginationParams(IntegrationCursor).fields,
  includeDeleted: Schema.optional(Schema.BooleanFromString),
});
export type GetTeamIntegrationsParams = typeof GetTeamIntegrationsParams.Type;

export const IntegrationGroup = HttpApiGroup.make("integration")
  .add(
    endpoint.merchant
      .get("getTeamIntegrations", "/integrations")

      .setUrlParams(GetTeamIntegrationsParams)
      .addSuccess(
        CursorPaginatedResponse(
          IntegrationWithChannelDataResponse,
          IntegrationCursor,
        ),
      )
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getIntegration", "/integrations/:id")

      .setPath(Schema.Struct({ id: ShopperAiIntegrationId }))
      .addSuccess(IntegrationWithChannelDataResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getIntegrationsByAccount", "/accounts/:accountId/integrations")

      .setPath(Schema.Struct({ accountId: AccountId }))
      .setUrlParams(CursorPaginationParams(IntegrationCursor))
      .addSuccess(
        CursorPaginatedResponse(
          IntegrationWithChannelDataResponse,
          IntegrationCursor,
        ),
      )
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("setIntegrationSettings", "/integrations/:id/settings")

      .setPath(Schema.Struct({ id: ShopperAiIntegrationId }))
      .setPayload(SetIntegrationSettingsPayload)
      .addSuccess(SetIntegrationSettingsResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .patch("updateContactFormIntegration", "/integrations/contact-form")
      .setPayload(UpdateContactFormIntegrationInput)
      .addSuccess(Schema.Struct({ ok: Schema.Literal(true) }))
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .prefix("/api");
